
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserFormData, UserFormSchema } from '../../types/form';
import { UsersDetail, CreateUserRequest } from '../../types/settings/UsersTypes';
import { toastMessage } from '@/utils/toast-message';
import { createUser, updateUser } from '@/actions/data-fetch-users';
import { getTeams } from '@/actions/data-fetch-teams';
import {
  User,
  Mail,
  Phone,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Trash2,
  Users,
  Tag,
  PhoneCall,
  Hash
} from 'lucide-react';
import RoleSelect from '../common/InputField/RoleSelect';
import optimizedApi from '@/utils/api/optimized-api';
import UserStatusHistory from './UserStatusHistory';

interface ModernUserFormProps {
  onClose?: () => void;
  uuid?: string;
  initialData?: UsersDetail | null;
  onSuccess: (user: UsersDetail) => void;
  onDelete?: (uuid: string) => void;
  disabledFields?: {
    phone?: boolean;
    role?: boolean;
  };
}

const ModernUserForm: React.FC<ModernUserFormProps> = ({
  onClose,
  uuid,
  initialData,
  onSuccess,
  onDelete,
  disabledFields = {}
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableTeams, setAvailableTeams] = useState<any[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [crmTags, setCrmTags] = useState<string[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; assignedCount: number; loading: boolean }>({
    open: false, assignedCount: 0, loading: false
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    register
  } = useForm<UserFormData>({
    resolver: zodResolver(UserFormSchema),
    defaultValues: {
      userName: '',
      userEmail: '',
      userPhone: '',
      gender: 'male',
      role_uuid: '',
      team_uuid: '',
      smartflo_login_id: '',
      caller_id: '',
      agent_discount_code: '',
      agent_crm_tag: '',
      leadNotifications: [],
      notificationsPaused: 'no',
      dailyEmailReports: []
    },
  });

  const notificationsPaused = watch('notificationsPaused');



  // Fetch available teams
  const fetchTeams = async () => {
    try {
      setLoadingTeams(true);
      const response = await getTeams({ limit: 100 });
      
      if (response.success || response.status === 'success') {
        const teams = response.data?.teams || [];
        setAvailableTeams(teams);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoadingTeams(false);
    }
  };

  /**
   * The tag list is the same one Create Order offers, on purpose. The agent's
   * attribution tag and the tag an order actually carries have to be the same
   * string -- the ETL joins them exactly -- so this is a pick-list, never free
   * text. Typing them separately is what let them drift apart before.
   */
  const fetchCrmTags = async () => {
    try {
      setLoadingTags(true);
      const res = await optimizedApi.get('/shopify/crm-tags');
      const rows = ((res as any)?.data?.data ?? (res as any)?.data ?? []) as Array<{ tag_name?: string; is_active?: boolean }>;
      setCrmTags(rows
        .filter((t) => t.is_active !== false)
        .map((t) => t.tag_name)
        .filter((n): n is string => !!n));
    } catch (error) {
      console.error('Error fetching CRM tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  useEffect(() => {
    fetchTeams();
    fetchCrmTags();
  }, []);

  useEffect(() => {
    if (initialData) {
      console.log('Setting form values with initialData:', initialData);
      setValue('userName', initialData.userName || '');
      setValue('userEmail', initialData.userEmail || '');
      setValue('userPhone', String(initialData.phone || ''));
      setValue('gender', initialData.gender || 'male');

      // Try role.uuid first, then fall back to role_uuid field directly
      const roleUuid = initialData.role?.uuid || (initialData as any).role_uuid || '';
      console.log('Setting role_uuid to:', roleUuid);
      setValue('role_uuid', roleUuid);

      setValue('team_uuid', initialData.team?.uuid || initialData.team_uuid || '');
      setValue('smartflo_login_id', (initialData as any).smartflo_login_id || '');
      setValue('caller_id', (initialData as any).caller_id || '');
      setValue('agent_discount_code', initialData.agent_discount_code || '');
      setValue('agent_crm_tag', (initialData as any).agent_crm_tag || '');

      const notifications: string[] = [];
      if (initialData.email_notification) notifications.push('email');
      if (initialData.sms_notification) notifications.push('sms');
      if (initialData.whatsapp_notifications) notifications.push('whatsapp');
      setValue('leadNotifications', notifications);

      setValue('notificationsPaused', initialData.user_pause ? 'yes' : 'no');
      setValue('dailyEmailReports', initialData.dailyEmailReports || []);
    }
  }, [initialData]);

  const transformFormToApiData = (formData: UserFormData): CreateUserRequest => {
    return {
      userName: formData.userName,
      userEmail: formData.userEmail,
      phone: formData.userPhone,
      gender: formData.gender,
      role_uuid: formData.role_uuid,
      team_uuid: formData.team_uuid || undefined,
      smartflo_login_id: (formData as any).smartflo_login_id || null,
      caller_id: (formData as any).caller_id || null,
      agent_discount_code: (formData as any).agent_discount_code || null,
      agent_crm_tag: (formData as any).agent_crm_tag || null,
      email_notification: formData.leadNotifications.includes('email'),
      sms_notification: formData.leadNotifications.includes('sms'),
      whatsapp_notifications: formData.leadNotifications.includes('whatsapp'),
      user_pause: formData.notificationsPaused === 'yes',
      // Status has its own switch (with a confirmation and a log). Saving this form must not
      // flip it: sending true here used to reactivate an inactive user on any edit.
      user_active: uuid ? (initialData?.user_active ?? true) : true,
    };
  };

  const onSubmit = async (formData: UserFormData) => {
    setIsSubmitting(true);
    try {
      const apiData = transformFormToApiData(formData);

      let response;
      if (uuid) {
        response = await updateUser(uuid, apiData);
      } else {
        response = await createUser(apiData);
      }

      if (response.status === 'success') {
        const role = {
          uuid: apiData.role_uuid,
          role_name: initialData?.role?.role_name || 'Role'
        };

        const updatedUser: UsersDetail = {
          ...initialData as UsersDetail,
          userName: apiData.userName,
          userEmail: apiData.userEmail,
          phone: apiData.phone,
          gender: apiData.gender,
          role,
          agent_discount_code: apiData.agent_discount_code ?? null,
          agent_crm_tag: (apiData as any).agent_crm_tag ?? null,
          smartflo_login_id: apiData.smartflo_login_id ?? null,
          caller_id: apiData.caller_id ?? null,
          email_notification: apiData.email_notification,
          sms_notification: apiData.sms_notification,
          whatsapp_notifications: apiData.whatsapp_notifications,
          user_pause: apiData.user_pause,
          user_active: apiData.user_active ?? true,
          dailyEmailReports: initialData?.dailyEmailReports || []
        };

        onSuccess(updatedUser);
      } else {
        throw new Error(response.message || 'Failed to save user');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to save user', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!uuid) return;
    setDeleteConfirm(s => ({ ...s, loading: true, open: false }));
    try {
      const res = await optimizedApi.get<{ count: number }>(`/users/${uuid}/assigned-leads-count`);
      const count = (res as any)?.data?.count ?? 0;
      setDeleteConfirm({ open: true, assignedCount: count, loading: false });
    } catch {
      setDeleteConfirm(s => ({ ...s, loading: false }));
      toastMessage('Failed to fetch lead count', 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!uuid) return;
    setIsDeleting(true);
    try {
      await optimizedApi.delete(`/users/${uuid}`);
      toastMessage('User deleted successfully', 'success');
      onDelete?.(uuid);
      onClose?.();
    } catch (e: any) {
      toastMessage(e.message || 'Failed to delete user', 'error');
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(s => ({ ...s, open: false }));
    }
  };

  const InputField = ({
    label,
    icon: Icon,
    error,
    disabled = false,
    description,
    children
  }: {
    label: string;
    icon: any;
    error?: string;
    disabled?: boolean;
    description?: string;
    children: React.ReactNode;
  }) => (
    <div className="space-y-2">
      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
        <Icon className="w-4 h-4 mr-2 text-gray-400" />
        {label}
        {disabled && <span className="ml-2 text-xs text-gray-500">(Read-only)</span>}
      </label>
      {children}
      {error && (
        <div className="flex items-center text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </div>
      )}
      {description && !error && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
      )}
    </div>
  );


  return (
    <div className="h-full flex flex-col relative">
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 flex items-center space-x-3">
            <Loader2 className="w-6 h-6 animate-spin text-brandColor" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {uuid ? 'Updating user...' : 'Creating user...'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Please wait while we save your changes
              </p>
            </div>
          </div>
        </div>
      )}
      

      {/* Form Content */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col min-h-0">
        <div className={`flex-1 overflow-y-auto p-6 transition-opacity ${isSubmitting ? 'pointer-events-none opacity-60' : ''}`}>
          <div className="space-y-5 max-w-2xl">

            {/* Full Name */}
            <InputField label="Full Name" icon={User} error={errors.userName?.message}>
              <input
                {...register('userName')}
                type="text"
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                placeholder="Enter full name"
              />
            </InputField>

            {/* Email */}
            <InputField label="Email Address" icon={Mail} error={errors.userEmail?.message}>
              <input
                {...register('userEmail')}
                type="email"
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                placeholder="user@example.com"
              />
            </InputField>

            {/* Phone + Gender */}
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Phone Number" icon={Phone} error={errors.userPhone?.message} disabled={disabledFields.phone}>
                <input
                  {...register('userPhone')}
                  type="tel"
                  autoComplete="off"
                  disabled={disabledFields.phone}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors ${
                    disabledFields.phone ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : ''
                  }`}
                  placeholder="Phone number"
                />
              </InputField>
              <InputField label="Gender" icon={User} error={errors.gender?.message}>
                <select
                  {...register('gender')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </InputField>
            </div>

            {/* Role + Team */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <RoleSelect
                  name="role_uuid"
                  control={control}
                  error={errors.role_uuid?.message}
                  required
                  disabled={disabledFields.role}
                  roleStyling="w-full"
                />
              </div>
              <InputField label="Team" icon={Users} error={errors.team_uuid?.message}>
                <select
                  {...register('team_uuid')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                  disabled={loadingTeams}
                >
                  <option value="">No Team</option>
                  {availableTeams.map((team) => (
                    <option key={team.uuid} value={team.uuid}>{team.team_name}</option>
                  ))}
                </select>
                {loadingTeams && (
                  <div className="flex items-center text-xs text-gray-500 mt-1">
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    Loading...
                  </div>
                )}
              </InputField>
            </div>

            {/* Telephony — Smartflo login ID + Caller ID */}
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Smartflo Login ID" icon={PhoneCall} error={(errors as any).smartflo_login_id?.message} description="Agent SR / login number">
                <input
                  {...register('smartflo_login_id' as any)}
                  type="text"
                  autoComplete="off"
                  placeholder="e.g. 0604813090069"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                />
              </InputField>
              <InputField label="Caller ID" icon={Hash} error={(errors as any).caller_id?.message} description="DID shown to customer">
                <input
                  {...register('caller_id' as any)}
                  type="text"
                  autoComplete="off"
                  placeholder="e.g. 918062750808"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                />
              </InputField>
            </div>

            {/* Agent Coupon Code */}
            <InputField label="Agent Coupon Code" icon={Tag} error={(errors as any).agent_discount_code?.message} description="Shopify discount code for order attribution">
              <input
                {...register('agent_discount_code' as any)}
                type="text"
                autoComplete="off"
                placeholder="e.g. AGENT-SARAH10"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
              />
            </InputField>

            {/* CRM Order Tag */}
            <InputField
              label="CRM Order Tag"
              icon={Tag}
              error={(errors as any).agent_crm_tag?.message}
              description="Shopify order tag that credits sales to this agent — must already exist under Shopify Admin → CRM Tags"
            >
              <select
                {...register('agent_crm_tag' as any)}
                disabled={loadingTags}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors disabled:opacity-60"
              >
                <option value="">{loadingTags ? 'Loading tags…' : '— No tag —'}</option>
                {crmTags.map((tag) => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </InputField>

            {/* Pause Lead Assignments */}
            <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-600">
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Pause Lead Assignments</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Paused users can log in but won't receive auto-assigned leads</p>
              <div className="flex items-center gap-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="no"
                    checked={notificationsPaused === 'no'}
                    onChange={() => setValue('notificationsPaused', 'no')}
                    className="h-4 w-4 text-brandColor focus:outline-none focus:ring-1 focus:ring-brandColor border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-500" /> Active
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="yes"
                    checked={notificationsPaused === 'yes'}
                    onChange={() => setValue('notificationsPaused', 'yes')}
                    className="h-4 w-4 text-brandColor focus:outline-none focus:ring-1 focus:ring-brandColor border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4 text-yellow-500" /> Paused
                  </span>
                </label>
              </div>
            </div>

            {uuid && <UserStatusHistory uuid={uuid} />}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between">
            {/* Delete — only in edit mode */}
            <div>
              {uuid && onDelete && (
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  disabled={deleteConfirm.loading || isDeleting}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {deleteConfirm.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete User
                </button>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brandColor border border-transparent rounded-lg hover:bg-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {isSubmitting ? (uuid ? 'Updating...' : 'Creating...') : (uuid ? 'Update User' : 'Create User')}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.open && (
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Delete User</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {deleteConfirm.assignedCount > 0
                    ? `${initialData?.userName || 'This user'} has ${deleteConfirm.assignedCount} assigned lead${deleteConfirm.assignedCount !== 1 ? 's' : ''}. They will be unassigned and appear under the Unassigned owner filter, so a super admin can assign them to other agents. The user's history is kept.`
                    : `Are you sure you want to delete ${initialData?.userName || 'this user'}? Their account will be deactivated and their history kept.`
                  }
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(s => ({ ...s, open: false }))}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-all"
              >
                {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernUserForm;